import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListFormPageHeader from '../../components/ListFormPageHeader';
import { ListPageFloatingControls } from '../../components/ListPageLayout';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { openPdfResponse } from '../../utils/openPdfInApp';
import {
    deleteConcreteEstimator,
    getConcreteEstimatorList,
    printConcreteEstimator,
} from '../../services/ConcreteEstimatorServices';
import { PROJECT_TYPES, formatKg, sandKgFromM3, aggregateKgFromM3 } from '../../utils/concreteEstimatorCalc';
import { formatCurrency, formatRatePerM3 } from '../../utils/concreteEstimatorRateCalc';

function ConcreteEstimatorTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const projectLabel = (value) => PROJECT_TYPES.find((p) => p.value === value)?.label || value;

    const columns = useMemo(
        () => [
            { headerName: 'Estimate No', fieldName: 'estimate_display' },
            { headerName: 'Date', fieldName: 'estimate_date' },
            { headerName: 'Project', fieldName: 'project_name' },
            { headerName: 'Type', fieldName: 'project_type_label' },
            { headerName: 'Grade', fieldName: 'concrete_grade' },
            { headerName: 'Volume (m³)', fieldName: 'total_volume_m3' },
            { headerName: 'Rate (/m³)', fieldName: 'quoted_rate_display' },
            { headerName: 'Cement (kg)', fieldName: 'total_cement_kg_display' },
            { headerName: 'Customer', fieldName: 'customer_name' },
            { headerName: 'Plant', fieldName: 'plant_name' },
            { headerName: 'ROW_ACTIONS', fieldName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const tableData = useMemo(
        () => (data.records || []).map((r) => ({
            ...r,
            estimate_display: `${r.prefix || ''}${r.estimate_no || ''}`,
            project_type_label: projectLabel(r.project_type),
            total_volume_m3: Number(r.total_volume_m3 || 0).toFixed(3),
            quoted_rate_display: r.quoted_rate_per_m3 ? formatRatePerM3(r.quoted_rate_per_m3) : '—',
            total_cement_kg_display: formatKg(r.total_cement_kg),
            total_sand_kg_display: formatKg(r.total_sand_kg ?? sandKgFromM3(r.total_sand_m3)),
            total_aggregate_kg_display: formatKg(r.total_aggregate_kg ?? aggregateKgFromM3(r.total_aggregate_m3)),
        })),
        [data.records],
    );

    useEffect(() => {
        setIsLoading(true);
        getConcreteEstimatorList(cookies)
            .then((res) => setData({ records: res.records || [] }))
            .catch((err) => displayError(err?.response?.data ?? err.message, 'Load failed'))
            .finally(() => setIsLoading(false));
    }, [cookies, refreshKey]);

    const onEdit = (id) => navigate(`/ConcreteEstimatorEdit/${id}`);
    const onDelete = (id) => {
        Swal.fire({ title: 'Delete this estimate?', showCancelButton: true, confirmButtonText: 'Delete' }).then((r) => {
            if (!r.isConfirmed) return;
            deleteConcreteEstimator(cookies, id)
                .then(() => { setRefreshKey((k) => k + 1); Swal.fire('Deleted', '', 'success'); })
                .catch((err) => displayError(err?.response?.data ?? err.message, 'Delete failed'));
        });
    };
    const onPrint = async (id) => {
        try {
            const res = await printConcreteEstimator(cookies, id);
            openPdfResponse(res, { title: 'Concrete Estimate PDF' });
        } catch (err) {
            let message = err?.response?.data ?? err.message;
            if (message instanceof Blob) {
                try {
                    const text = await message.text();
                    message = JSON.parse(text);
                } catch {
                    message = 'PDF generation failed on server.';
                }
            }
            displayError(message, 'Print failed');
        }
    };

    const handleDownloadCsv = () => {
        exportAsCSV(
            data.records.map((r) => ({
                estimate_no: `${r.prefix || ''}${r.estimate_no || ''}`,
                date: r.estimate_date,
                project: r.project_name,
                type: projectLabel(r.project_type),
                grade: r.concrete_grade,
                volume_m3: r.total_volume_m3,
                cement_kg: r.total_cement_kg,
                sand_kg: r.total_sand_kg ?? sandKgFromM3(r.total_sand_m3),
                aggregate_kg: r.total_aggregate_kg ?? aggregateKgFromM3(r.total_aggregate_m3),
                customer: r.customer_name,
                plant: r.plant_name,
            })),
            'ConcreteEstimates.csv',
        );
    };

    const handleDownloadPdf = () => {
        exportAsPDF(
            'List of Concrete Estimates',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            tableData,
            'ConcreteEstimates.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/ConcreteEstimator"
                    addNewLabel="New Estimate"
                    homeLink="/Home"
                />
                <ListFormPageHeader
                    module="MARKETTING"
                    title="List of Concrete Estimates"
                    subtitle="Solar Ready Mix concrete quantity estimates for house, school, commercial, road, dam and other projects."
                />
                <div className="container item-list-table-container">
                    <div className="mb-2">
                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={() => navigate('/ConcreteEstimator')}>New Estimate</button>
                        <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={handleDownloadCsv}>Download CSV</button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={handleDownloadPdf}>Download PDF</button>
                    </div>
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="Concrete Estimates"
                            columns={columns}
                            data={tableData}
                            onRowEdit={onEdit}
                            onRowDelete={onDelete}
                            onRowPrint={onPrint}
                            hidePrint={false}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConcreteEstimatorTable;
